import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import WizardSelect from '../../components/ui/WizardSelect';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../../components/criminal/CriminalReviewUi';
import {
  getRightToInformationRequestViewData,
  saveRightToInformationRequest,
  type RightToInformationRequestViewData,
} from '../../services/rightToInformationCaseRequests';
import type { RightToInformationWizardStep } from '../../pages/wizard/rightToInformation/rightToInformationConstants';
import type { RightToInformationWizardDraft } from '../../types/rightToInformationCaseRequest';
import {
  isHighUrgencyLevel,
  minDeadlineDateForRightToInformation,
  validateRightToInformationStep,
} from '../../pages/wizard/rightToInformation/rightToInformationStepValidation';
import {
  preferredAccessLabels,
  translatePreferredAccessName,
} from '../../pages/wizard/rightToInformation/rightToInformationHelpers';
import { useRightToInformationLookups } from '../../pages/wizard/rightToInformation/useRightToInformationLookups';
import { useCaseCategorization } from '../../pages/wizard/rightToInformation/useCaseCategorization';
import { submitterFromApiStep } from '../view-request/viewRequestHelpers';
import { contactNumberError, sliceMax } from '../../utils/caseFieldValidation';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid } from '../../utils/requiredValidation';
import { FieldError, inpClass } from './criminalFieldUi';
import {
  confirmCaseUpdateCancel,
  formatSubmittedAtLabel,
  isCaseRequestUuid,
  mergeSavedStepIntoInitialBaseline,
  toSelectOpts,
} from './taskCaseUpdateHelpers';
import TaskCaseUpdateSectionActions from './TaskCaseUpdateSectionActions';

type Props = {
  caseRequestId: string;
  recentSubmittedAt: string;
  readOnly?: boolean;
  viewData?: RightToInformationRequestViewData | null;
  viewDataLoading?: boolean;
  onCancelConfirmed?: () => void;
};

const MAX_NAME = 2000;
const MAX_SHORT = 255;
const MAX_CONTACT = 10;
const MAX_TEXT = 2000;

function cloneDraft(d: RightToInformationWizardDraft): RightToInformationWizardDraft {
  return {
    ...d,
    applicableAreas: [...d.applicableAreas],
    applicableAreaIds: [...d.applicableAreaIds],
    preferredMethodOfAccessIds: [...d.preferredMethodOfAccessIds],
    preferredMethodOfAccessNames: [...d.preferredMethodOfAccessNames],
    documents: d.documents.map((doc) => ({ ...doc })),
  };
}

function draftHasUnsavedChanges(
  current: RightToInformationWizardDraft,
  saved: RightToInformationWizardDraft,
): boolean {
  return JSON.stringify(current) !== JSON.stringify(saved);
}

function pickStep1Snapshot(d: RightToInformationWizardDraft) {
  return {
    requestorName: d.requestorName,
    nicOrPassportNumber: d.nicOrPassportNumber,
    address: d.address,
    contactNumber: d.contactNumber,
    emailAddress: d.emailAddress,
    isSriLankanCitizen: d.isSriLankanCitizen,
    specificPublicAuthorityName: d.specificPublicAuthorityName,
    requestedInformationDetails: d.requestedInformationDetails,
    informationRequestedTimePeriod: d.informationRequestedTimePeriod,
    preferredMethodOfAccessIds: d.preferredMethodOfAccessIds,
    preferredLanguageId: d.preferredLanguageId,
    involvesLifeOrPersonalLiberty: d.involvesLifeOrPersonalLiberty,
    justification: d.justification,
    additionalNotes: d.additionalNotes,
  };
}

function pickStep2Snapshot(d: RightToInformationWizardDraft) {
  return {
    urgencyLevelId: d.urgencyLevelId,
    deadline: d.deadline,
    urgencyJustification: d.urgencyJustification,
  };
}

function stepHasData(step: 1 | 2, d: RightToInformationWizardDraft): boolean {
  if (step === 1) {
    return !!(
      d.requestorName.trim() ||
      d.nicOrPassportNumber.trim() ||
      d.address.trim() ||
      d.contactNumber.trim() ||
      d.emailAddress.trim() ||
      d.isSriLankanCitizen ||
      d.specificPublicAuthorityName.trim() ||
      d.requestedInformationDetails.trim() ||
      d.informationRequestedTimePeriod.trim() ||
      d.preferredMethodOfAccessIds.length ||
      d.preferredLanguageId.trim() ||
      d.involvesLifeOrPersonalLiberty ||
      d.justification.trim() ||
      d.additionalNotes.trim()
    );
  }
  return !!(d.urgencyLevelId.trim() || d.deadline.trim() || d.urgencyJustification.trim());
}

function canSaveStep(
  step: 1 | 2,
  draft: RightToInformationWizardDraft | null,
  initial: RightToInformationWizardDraft | null,
): boolean {
  if (!draft || !initial) return false;
  const pick = step === 1 ? pickStep1Snapshot : pickStep2Snapshot;
  const changed = JSON.stringify(pick(draft)) !== JSON.stringify(pick(initial));
  return (stepHasData(step, draft) || stepHasData(step, initial)) && changed;
}

export default function TaskCaseRightToInformationUpdateTab({
  caseRequestId,
  recentSubmittedAt,
  readOnly = false,
  viewData,
  viewDataLoading = false,
  onCancelConfirmed,
}: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<RightToInformationWizardDraft | null>(null);
  const [initial, setInitial] = useState<RightToInformationWizardDraft | null>(null);
  const [step3, setStep3] = useState<RightToInformationRequestViewData['step3']>();
  const [submittedLabel, setSubmittedLabel] = useState(recentSubmittedAt);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [savingStep, setSavingStep] = useState<RightToInformationWizardStep | null>(null);
  const saveInFlightRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isSaving = savingStep !== null;

  const lookups = useRightToInformationLookups('review');
  const cat = useCaseCategorization(draft?.fileCategoryId ?? '', draft?.subFileCategoryId ?? '');
  const minDeadlineDate = useMemo(() => minDeadlineDateForRightToInformation(), []);
  const useSharedViewData = viewData !== undefined || viewDataLoading;
  const loadKey = `${caseRequestId}|${recentSubmittedAt}|${viewData?.submittedAt ?? ''}|${viewData?.draft.draftRequestId ?? ''}`;
  const loading = useSharedViewData
    ? viewDataLoading || loadedForKey !== loadKey
    : loadedForKey !== loadKey;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isCaseRequestUuid(caseRequestId)) {
        if (cancelled) return;
        setDraft(null);
        setInitial(null);
        setLoadError('Case request id is missing or invalid.');
        setLoadedForKey(loadKey);
        return;
      }

      if (useSharedViewData) {
        if (viewDataLoading) return;
        if (!viewData?.draft) {
          if (cancelled) return;
          setDraft(null);
          setInitial(null);
          setStep3(undefined);
          setLoadError('Could not load case update data.');
          setLoadedForKey(loadKey);
          return;
        }
        if (cancelled) return;
        const loaded = cloneDraft(viewData.draft);
        setDraft(loaded);
        setInitial(cloneDraft(loaded));
        setStep3(viewData.step3);
        setSubmittedLabel(formatSubmittedAtLabel(viewData.submittedAt, recentSubmittedAt));
        setLoadError('');
        setLoadedForKey(loadKey);
        return;
      }

      const res = await getRightToInformationRequestViewData(caseRequestId);
      if (cancelled) return;
      if (!res.success || !res.data?.draft) {
        setDraft(null);
        setInitial(null);
        setStep3(undefined);
        setLoadError((!res.success ? res.message : undefined) ?? 'Could not load case update data.');
        setLoadedForKey(loadKey);
        return;
      }
      const loaded = cloneDraft(res.data.draft);
      setDraft(loaded);
      setInitial(cloneDraft(loaded));
      setStep3(res.data.step3);
      setSubmittedLabel(formatSubmittedAtLabel(res.data.submittedAt, recentSubmittedAt));
      setLoadError('');
      setLoadedForKey(loadKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [caseRequestId, loadKey, recentSubmittedAt, useSharedViewData, viewData, viewDataLoading]);

  const canSaveStep1 = useMemo(() => canSaveStep(1, draft, initial), [draft, initial]);
  const canSaveStep2 = useMemo(() => canSaveStep(2, draft, initial), [draft, initial]);
  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));

  const togglePreferredAccess = (id: string) => {
    setDraft((d) => {
      if (!d) return d;
      const has = d.preferredMethodOfAccessIds.includes(id);
      const ids = has
        ? d.preferredMethodOfAccessIds.filter((x) => x !== id)
        : [...d.preferredMethodOfAccessIds, id];
      const names = preferredAccessLabels(ids, lookups.preferredMethodOfAccess);
      return { ...d, preferredMethodOfAccessIds: ids, preferredMethodOfAccessNames: names };
    });
    clearErr('preferredMethodOfAccessIds');
  };

  const saveSection = async (step: 1 | 2) => {
    if (!draft || readOnly || saveInFlightRef.current) return;
    const selectedFileCategory = cat.fileCategories.find((c) => c.id === draft.fileCategoryId);
    const result = validateRightToInformationStep(step, draft, {
      subCategories: cat.subCategories,
      loadingSubCategories: cat.loadingSubCategories,
      fileCategoryCode: selectedFileCategory?.code,
    });
    setErrors(result.errors);
    if (!result.valid) {
      focusFirstInvalid(result.specs, result.errors);
      return;
    }

    saveInFlightRef.current = true;
    setSavingStep(step);
    try {
      const r = await saveRightToInformationRequest(draft, {
        isDraft: false,
        confirmedCorrect: true,
        currentStep: step,
        isUpdateAlreadySubmitted: true,
      });
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Failed to save case update.', variant: 'error' });
        return;
      }
      const next = {
        ...draft,
        draftRequestId: r.data.requestId || draft.draftRequestId,
        referenceNo: r.data.fileReferenceNumber ?? draft.referenceNo,
      };
      setDraft(next);
      setInitial((prev) => {
        if (!prev) return cloneDraft(next);
        const pick = step === 1 ? pickStep1Snapshot : pickStep2Snapshot;
        return mergeSavedStepIntoInitialBaseline(prev, next, pick, cloneDraft);
      });
      setErrors({});
      setFlashToast({ message: 'Case update saved successfully.', variant: 'success' });
    } finally {
      saveInFlightRef.current = false;
      setSavingStep(null);
    }
  };

  const resetSection = () => {
    if (initial) {
      setDraft(cloneDraft(initial));
      setErrors({});
    }
  };

  const beginCancel = () => {
    if (isSaving || !draft || !initial) return;
    if (draftHasUnsavedChanges(draft, initial)) {
      setCancelConfirmOpen(true);
      return;
    }
    resetSection();
  };

  if (loading) return <p className="tcd-update-ts">Loading case update…</p>;
  if (!draft) {
    return (
      <div className="tcd-case-update-error" role="alert">
        <p className="tcd-update-ts" style={{ color: 'var(--red)', margin: 0 }}>
          {loadError || 'Could not load case update data.'}
        </p>
      </div>
    );
  }

  const submitter = submitterFromApiStep(step3);
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);
  const justificationRequired = draft.involvesLifeOrPersonalLiberty === 'yes';

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          confirmCaseUpdateCancel(resetSection, () => setCancelConfirmOpen(false), onCancelConfirmed);
        }}
      />

      <div className="tcd-case-update">
        <p className="tcd-update-ts">
          Request Submitted at:{' '}
          <strong>{submittedLabel || formatDisplayDateTime(recentSubmittedAt) || '—'}</strong>
        </p>

        <ReviewSection step="Step 01" title="Case Overview">
          <ReviewSubBlock title="Case Categorization">
            <ReviewGrid cols={4}>
              <ReviewField label="File Category" value={draft.fileCategory} />
              <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            </ReviewGrid>
            <ReviewChips items={draft.applicableAreas} />
          </ReviewSubBlock>

          <ReviewSubBlock title="Requester Details">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Name of Requestor <span className="req">*</span>
                </label>
                <input
                  id="rti-requestorName"
                  className={inpClass(Boolean(errors.requestorName))}
                  type="text"
                  maxLength={MAX_NAME}
                  value={draft.requestorName}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, requestorName: sliceMax(e.target.value, MAX_NAME) } : d,
                    );
                    clearErr('requestorName');
                  }}
                />
                <FieldError message={errors.requestorName} />
              </div>
              <div className="ccr-fg">
                <label>
                  NIC/ Passport Number <span className="req">*</span>
                </label>
                <input
                  id="rti-nicOrPassport"
                  className={inpClass(Boolean(errors.nicOrPassportNumber))}
                  type="text"
                  maxLength={MAX_SHORT}
                  value={draft.nicOrPassportNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, nicOrPassportNumber: sliceMax(e.target.value, MAX_SHORT) } : d,
                    );
                    clearErr('nicOrPassportNumber');
                  }}
                />
                <FieldError message={errors.nicOrPassportNumber} />
              </div>
            </div>

            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Address <span className="req">*</span>
              </label>
              <textarea
                id="rti-address"
                className={inpClass(Boolean(errors.address))}
                rows={3}
                maxLength={MAX_NAME}
                value={draft.address}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, address: sliceMax(e.target.value, MAX_NAME) } : d));
                  clearErr('address');
                }}
              />
              <FieldError message={errors.address} />
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>Contact Number</label>
                <input
                  id="rti-contactNumber"
                  className={inpClass(Boolean(errors.contactNumber))}
                  type="text"
                  inputMode="numeric"
                  value={draft.contactNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT);
                    setDraft((d) => (d ? { ...d, contactNumber: digits } : d));
                    const err = contactNumberError(digits);
                    setErrors((prev) => ({ ...prev, contactNumber: err ?? '' }));
                  }}
                />
                <FieldError message={errors.contactNumber} />
              </div>
              <div className="ccr-fg">
                <label>Email Address</label>
                <input
                  id="rti-emailAddress"
                  className={inpClass(Boolean(errors.emailAddress))}
                  type="email"
                  maxLength={MAX_SHORT}
                  value={draft.emailAddress}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, emailAddress: sliceMax(e.target.value, MAX_SHORT) } : d,
                    );
                    clearErr('emailAddress');
                  }}
                />
                <FieldError message={errors.emailAddress} />
              </div>
            </div>

            <div className="ccr-fg" id="rti-citizen">
              <label>
                Is the requestor a citizen of Sri Lanka? <span className="req">*</span>
              </label>
              <div className="ccr-radio-row">
                {(['yes', 'no'] as const).map((v) => (
                  <label key={v} className="ccr-radio">
                    <input
                      type="radio"
                      name="rti-citizen"
                      checked={draft.isSriLankanCitizen === v}
                      disabled={readOnly}
                      onChange={() => {
                        setDraft((d) => (d ? { ...d, isSriLankanCitizen: v } : d));
                        clearErr('isSriLankanCitizen');
                      }}
                    />
                    {v === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
              <FieldError message={errors.isSriLankanCitizen} />
            </div>
          </ReviewSubBlock>

          <ReviewSubBlock title="Information Request Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Specific Public Authority Name <span className="req">*</span>
              </label>
              <input
                id="rti-publicAuthority"
                className={inpClass(Boolean(errors.specificPublicAuthorityName))}
                type="text"
                maxLength={MAX_SHORT}
                value={draft.specificPublicAuthorityName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d
                      ? { ...d, specificPublicAuthorityName: sliceMax(e.target.value, MAX_SHORT) }
                      : d,
                  );
                  clearErr('specificPublicAuthorityName');
                }}
              />
              <FieldError message={errors.specificPublicAuthorityName} />
            </div>

            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Requested Information Details <span className="req">*</span>
              </label>
              <textarea
                id="rti-requestedDetails"
                className={inpClass(Boolean(errors.requestedInformationDetails))}
                rows={4}
                maxLength={MAX_NAME}
                value={draft.requestedInformationDetails}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          requestedInformationDetails: sliceMax(e.target.value, MAX_NAME),
                        }
                      : d,
                  );
                  clearErr('requestedInformationDetails');
                }}
              />
              <FieldError message={errors.requestedInformationDetails} />
            </div>

            <div className="ccr-fg">
              <label>Information Requested Time Period</label>
              <input
                className={inpClass(Boolean(errors.informationRequestedTimePeriod))}
                type="text"
                maxLength={MAX_SHORT}
                value={draft.informationRequestedTimePeriod}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          informationRequestedTimePeriod: sliceMax(e.target.value, MAX_SHORT),
                        }
                      : d,
                  );
                  clearErr('informationRequestedTimePeriod');
                }}
              />
              <FieldError message={errors.informationRequestedTimePeriod} />
            </div>
          </ReviewSubBlock>

          <ReviewSubBlock title="Additional Information">
            <div id="rti-preferredAccess" style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 10 }}>
                Preferred Method of Access <span className="req">*</span>
              </label>
              <div className="ccr-check-grid">
                {lookups.preferredMethodOfAccess.map((m) => (
                  <label key={m.id} className="ccr-check">
                    <input
                      type="checkbox"
                      checked={draft.preferredMethodOfAccessIds.includes(m.id)}
                      disabled={readOnly}
                      onChange={() => togglePreferredAccess(m.id)}
                    />
                    <span>{translatePreferredAccessName(m.name, t)}</span>
                  </label>
                ))}
              </div>
              <FieldError message={errors.preferredMethodOfAccessIds} />
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Preferred Language for Response <span className="req">*</span>
                </label>
                <WizardSelect
                  id="rti-preferredLanguage"
                  hasError={Boolean(errors.preferredLanguageId)}
                  value={draft.preferredLanguageId}
                  disabled={readOnly}
                  placeholder="Select Preferred Language"
                  options={toSelectOpts(lookups.preferredLanguages)}
                  onChange={(id) => {
                    const name = lookups.preferredLanguages.find((l) => l.id === id)?.name ?? '';
                    setDraft((d) =>
                      d ? { ...d, preferredLanguageId: id, preferredLanguageName: name } : d,
                    );
                    clearErr('preferredLanguageId');
                  }}
                />
                <FieldError message={errors.preferredLanguageId} />
              </div>
            </div>

            <div className="ccr-fg" id="rti-lifeLiberty" style={{ marginBottom: 14 }}>
              <label>
                Does this request involve life or personal liberty of a citizen?{' '}
                <span className="req">*</span>
              </label>
              <div className="ccr-radio-row">
                {(['yes', 'no'] as const).map((v) => (
                  <label key={v} className="ccr-radio">
                    <input
                      type="radio"
                      name="rti-lifeLiberty"
                      checked={draft.involvesLifeOrPersonalLiberty === v}
                      disabled={readOnly}
                      onChange={() => {
                        setDraft((d) => (d ? { ...d, involvesLifeOrPersonalLiberty: v } : d));
                        clearErr('involvesLifeOrPersonalLiberty');
                        if (v !== 'yes') clearErr('justification');
                      }}
                    />
                    {v === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
              <FieldError message={errors.involvesLifeOrPersonalLiberty} />
            </div>

            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Justification {justificationRequired ? <span className="req">*</span> : null}
              </label>
              <textarea
                id="rti-justification"
                className={inpClass(Boolean(errors.justification))}
                rows={3}
                maxLength={MAX_NAME}
                value={draft.justification}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, justification: sliceMax(e.target.value, MAX_NAME) } : d,
                  );
                  clearErr('justification');
                }}
              />
              <FieldError message={errors.justification} />
            </div>

            <div className="ccr-fg">
              <label>Additional Notes</label>
              <textarea
                className={inpClass(Boolean(errors.additionalNotes))}
                rows={3}
                maxLength={MAX_NAME}
                value={draft.additionalNotes}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, additionalNotes: sliceMax(e.target.value, MAX_NAME) } : d,
                  );
                  clearErr('additionalNotes');
                }}
              />
              <FieldError message={errors.additionalNotes} />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep1}
            saving={savingStep === 1}
            readOnly={readOnly}
            onSave={() => void saveSection(1)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 02" title="Timeline">
          <ReviewSubBlock title="Timeline">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Urgency Level <span className="req">*</span>
                </label>
                <WizardSelect
                  id="rti-urgency"
                  hasError={Boolean(errors.urgencyLevelId)}
                  value={draft.urgencyLevelId}
                  disabled={readOnly}
                  placeholder="Select Urgency Level"
                  options={toSelectOpts(lookups.urgencyLevels)}
                  onChange={(id) => {
                    const name = lookups.urgencyLevels.find((u) => u.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, urgencyLevelId: id, urgencyLevelName: name } : d));
                    if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                    clearErr('urgencyLevelId');
                  }}
                />
                <FieldError message={errors.urgencyLevelId} />
              </div>
              <div className="ccr-fg">
                <label>Deadline (if any)</label>
                <FormattedDateInput
                  id="rti-deadline"
                  className={inpClass(Boolean(errors.deadline))}
                  value={draft.deadline}
                  minDate={minDeadlineDate}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(next) => {
                    setDraft((d) => (d ? { ...d, deadline: next } : d));
                    clearErr('deadline');
                  }}
                />
                <FieldError message={errors.deadline} />
              </div>
            </div>
            <div className="ccr-fg">
              <label>
                Urgency Justification{' '}
                {urgencyJustificationRequired ? <span className="req">*</span> : null}
              </label>
              <textarea
                id="rti-urgencyJustification"
                className={inpClass(Boolean(errors.urgencyJustification))}
                rows={3}
                maxLength={MAX_TEXT}
                value={draft.urgencyJustification}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, urgencyJustification: sliceMax(e.target.value, MAX_TEXT) } : d,
                  );
                  clearErr('urgencyJustification');
                }}
              />
              <FieldError message={errors.urgencyJustification} />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep2}
            saving={savingStep === 2}
            readOnly={readOnly}
            onSave={() => void saveSection(2)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 03" title="Submission">
          <ReviewSubBlock title="Request Submitter's Details">
            <ReviewGrid cols={4}>
              <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
              <ReviewField label="Designation" value={submitter.designation} />
              <ReviewField label="Submitted by" value={submitter.name} />
              <ReviewField label="Email Address" value={submitter.email} />
              <ReviewField label="Contact Number" value={submitter.contact} />
            </ReviewGrid>
          </ReviewSubBlock>
        </ReviewSection>
      </div>
    </>
  );
}
